import React from 'react';
import { Card } from '../ui/card';
import { getFitColor } from '../../lib/scoreUtils';

const pct = (v) => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`);
const num = (v) => (v === null || v === undefined ? '—' : v);

const ROLE_LABELS = {
  technical: 'Technical / IC', sales: 'Sales / Revenue',
  leadership: 'Leadership / Exec', general: 'General',
};

function Kpi({ label, value, hint, tone = '#002A38', testId }) {
  return (
    <Card data-testid={testId} style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.25rem' }}>
      <div style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B' }}>{label}</div>
      <div style={{ fontSize: '1.75rem', fontWeight: 800, fontFamily: 'Cabinet Grotesk', color: tone, marginTop: '0.25rem', lineHeight: 1 }}>{value}</div>
      {hint && <div style={{ fontSize: '0.6875rem', color: '#94A3B8', marginTop: '0.375rem' }}>{hint}</div>}
    </Card>
  );
}

function EmptyState() {
  return (
    <Card data-testid="scoring-accuracy-empty" style={{ background: 'hsl(var(--card))', border: '1px dashed hsl(var(--border))', borderRadius: 14, padding: '2.5rem', textAlign: 'center' }}>
      <div style={{ fontSize: '1rem', fontWeight: 700, fontFamily: 'Cabinet Grotesk', marginBottom: '0.5rem' }}>Collecting ground-truth — no labels yet</div>
      <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', maxWidth: 560, margin: '0 auto', lineHeight: 1.6 }}>
        Accuracy is measured against real recruiter behaviour. As recruiters <strong>advance</strong> candidates
        (assessment → interview → offer → hired) or mark them <strong>rejected</strong>, and give
        👍/👎 feedback on the AI fit, this page will fill with precision, recall, AUC, calibration and
        over/under-scoring metrics. No manual labelling required.
      </p>
    </Card>
  );
}

const aucTone = (v) => (v == null ? '#002A38' : v >= 0.8 ? '#059669' : v >= 0.65 ? '#D97706' : '#DC2626');

export function ScoringAccuracyTab({ data }) {
  if (!data) return <div style={{ color: '#64748B', padding: '2rem' }}>Loading accuracy metrics…</div>;

  const b = data.behavioural || { n: 0 };
  const f = data.feedback || { n: 0 };

  if ((b.n || 0) === 0 && (f.n || 0) === 0) {
    return (
      <div data-testid="scoring-accuracy-tab" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <Intro version={data.scoring_version} totalScored={data.total_scored} />
        <EmptyState />
      </div>
    );
  }

  return (
    <div data-testid="scoring-accuracy-tab" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <Intro version={data.scoring_version} totalScored={data.total_scored} />

      {b.n > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
            <Kpi testId="kpi-labeled-decisions" label="Labeled decisions" value={num(b.n)} hint={`${b.advanced} advanced · ${b.rejected} rejected`} />
            <Kpi testId="kpi-auc" label="Ranking AUC" value={num(b.auc)} tone={aucTone(b.auc)} hint="1.0 = perfect ranking · 0.5 = chance" />
            <Kpi testId="kpi-precision" label="Precision" value={pct(b.precision)} hint={`of ≥${data.threshold} predicted good`} />
            <Kpi testId="kpi-recall" label="Recall" value={pct(b.recall)} hint="of advanced candidates caught" />
            <Kpi testId="kpi-separation" label="Score separation" value={b.separation == null ? '—' : `+${b.separation}`} hint={`adv ${num(b.mean_score_advanced)} vs rej ${num(b.mean_score_rejected)}`} />
          </div>

          {/* Calibration by band */}
          <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
            <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '0.35rem' }}>Calibration by fit band</h3>
            <p style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '1rem' }}>Advance-rate should climb as the band improves. A flat or inverted curve means the bands aren't discriminating.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }} data-testid="calibration-list">
              {(b.calibration || []).map((c) => (
                <div key={c.band} data-testid={`calibration-${c.band}`} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ width: 96, fontSize: '0.75rem', fontWeight: 700, color: getFitColor(c.band) }}>{c.band}</span>
                  <div style={{ flex: 1, background: '#F1F5F9', borderRadius: 999, height: 16, overflow: 'hidden', position: 'relative' }}>
                    <div style={{ width: c.advance_rate == null ? '0%' : `${Math.round(c.advance_rate * 100)}%`, height: '100%', background: getFitColor(c.band), transition: 'width .4s ease' }} />
                  </div>
                  <span style={{ width: 120, textAlign: 'right', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                    {c.advance_rate == null ? 'no data' : `${pct(c.advance_rate)} · n=${c.n}`}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* By role type */}
          {b.by_role_type && Object.keys(b.by_role_type).length > 0 && (
            <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
              <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Accuracy by role type</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }} data-testid="role-type-breakdown">
                {Object.entries(b.by_role_type).map(([rt, v]) => (
                  <div key={rt} data-testid={`role-breakdown-${rt}`} style={{ border: '1px solid #EEF2FF', borderRadius: 10, padding: '0.875rem', background: '#F8FAFF' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#3730A3' }}>{ROLE_LABELS[rt] || rt}</div>
                    <div style={{ fontSize: '0.6875rem', color: '#64748B', marginTop: '0.35rem' }}>n={v.n} · advance {pct(v.advance_rate)} · avg score {num(v.mean_score)}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Recruiter feedback panel */}
      <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }} data-testid="feedback-panel">
        <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Recruiter feedback</h3>
        {f.n === 0 ? (
          <p style={{ fontSize: '0.8125rem', color: '#94A3B8' }}>No 👍/👎 feedback recorded yet.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
            <Kpi testId="kpi-agreement" label="Agreement rate" value={pct(f.agreement_rate)} tone={f.agreement_rate >= 0.8 ? '#059669' : f.agreement_rate >= 0.6 ? '#D97706' : '#DC2626'} hint={`${f.agreed}/${f.n} agreed`} />
            <Kpi testId="kpi-overscored" label="Engine too high" value={num(f.over_scored)} tone="#DC2626" hint="recruiter lowered fit" />
            <Kpi testId="kpi-underscored" label="Engine too low" value={num(f.under_scored)} tone="#D97706" hint="recruiter raised fit" />
            <Kpi testId="kpi-feedback-count" label="Feedback total" value={num(f.n)} hint="👍/👎 responses" />
          </div>
        )}
      </Card>
    </div>
  );
}

function Intro({ version, totalScored }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
      <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', maxWidth: 640, lineHeight: 1.6, margin: 0 }}>
        Measures how well AI scores predict real hiring decisions. Labels come automatically from recruiter
        actions — advancing a candidate = positive, rejecting = negative — plus explicit 👍/👎 feedback.
      </p>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
        {version && <span style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '0.25rem 0.6rem', borderRadius: 999, background: '#E0F2FE', color: '#005A80' }}>{version}</span>}
        <span style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>{totalScored ?? 0} scored candidates</span>
      </div>
    </div>
  );
}
